<template>
  <div
    class="hide-capture flex items-center gap-x-2"
    data-html2canvas-ignore
  >
    <IconButton
      :id="downloadButtonId"
      class="option-button desktop"
      icon="download"
      color="primary"
      size="lg"
      @click="showDownloadOptions = !showDownloadOptions"
      :active="showDownloadOptions"
    />
    <IconButton
      icon="expand"
      class="option-button desktop"
      color="primary"
      size="lg"
      @click="openChartWindow"
    />
    <IconButton
      :id="shareButtonId"
      class="option-button desktop"
      icon="share"
      color="primary"
      size="lg"
      @click="showSocialShareModal = !showSocialShareModal"
      :active="showSocialShareModal"
    />
    <IconButton
      :id="optionsButtonId"
      class="option-button mobile"
      icon="ellipsis-h"
      color="primary"
      size="lg"
      @click="showAllOptions = !showAllOptions"
      :active="showAllOptions"
    />
  </div>
  <ClientOnly>
    <Teleport :to="`#${downloadButtonId}`">
      <div
        v-if="showDownloadOptions"
        class="options-list"
        ref="downloadOptionsRef"
      >
        <ChartOptionButton
          v-for="option in downloadOptionsList"
          :key="option.label"
          @click="option.action"
          :icon="option.icon"
          :label="option.label"
        />
      </div>
    </Teleport>
    <Teleport :to="isMobile ? 'body' : `#${shareButtonId}`">
      <SocialShareModal
        v-model="showSocialShareModal"
        :share-url="shareUrl"
        class="share-modal"
      />
    </Teleport>
    <Teleport :to="`#${optionsButtonId}`">
      <div
        v-if="showAllOptions"
        class="options-list"
        ref="optionsRef"
      >
        <ChartOptionButton
          v-for="option in allOptionsList"
          :key="option.label"
          @click="option.action"
          :icon="option.icon"
          :label="option.label"
        />
      </div>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import { onClickOutside, useMediaQuery } from '@vueuse/core'

import ChartOptionButton from '~/components/@pages/analytics/ChartOptionButton.vue'
import IconButton from '~/components/button/IconButton.vue'
import SocialShareModal from '~/components/modal/SocialShareModal.vue'
import { useDownloadChart } from '~/composables/useDownloadChart'
import { IAnalyticsChartInfoOption, IAnalyticsParam, IAnalyticsPeriod } from '~/types/analytics.type'

interface IProps {
  name: string
  api: IAnalyticsChartInfoOption['api']
  chartRef: HTMLElement
  filename: string
  chartDownloadParam: IAnalyticsParam
}

const props = defineProps<IProps>()

const chartDownloadParamRef = toRef(props, 'chartDownloadParam')
const chartContainerRef = toRef(props, 'chartRef')
const filenameRef = toRef(props, 'filename')

const shareUrl = `${import.meta.env.VITE_APP_BASE_URL}${routes.getChartPage(props.name)}`
const downloadButtonId = `${props.name}-download-button`
const shareButtonId = `${props.name}-share-button`
const optionsButtonId = `${props.name}-options-button`

const downloadOptionsRef = ref<HTMLElement | null>(null)
const optionsRef = ref<HTMLElement | null>(null)
const showDownloadOptions = ref(false)
const showSocialShareModal = ref(false)
const showAllOptions = ref(false)
const isDownloadReady = ref(false)

const { data: allData } = props.api(chartDownloadParamRef, isDownloadReady)
const { isMobile } = useResponsiveScreen()

const { downloadChartToCSV, downloadChartToImage } = useDownloadChart(filenameRef)

const allDataList = computed(() => {
  if (!allData.value) return []
  return allData.value.chartDataList
})

watch([ isDownloadReady, allData ], () => {
  if (isDownloadReady.value && allData.value) {
    downloadChartToCSV(allDataList)
    isDownloadReady.value = showDownloadOptions.value = showAllOptions.value = false
  }
}, { deep: true })

type IOptions = {
  label: string
  icon: string
  action: (...params: any[]) => unknown
}

const downloadOptionsList = computed<Array<IOptions>>(() => {
  return [
    {
      label: 'CSV Download',
      icon: 'table',
      action: () => isDownloadReady.value = true
    },
    {
      label: 'PNG Download',
      icon: 'image',
      action: () => {
        downloadChartToImage(chartContainerRef, {
          height: chartContainerRef.value.clientHeight - 40, // period 선택하는 곳 높이를 빼줌
          backgroundColor: '#1C2634'
        })
        showDownloadOptions.value = false
        showAllOptions.value = false
      }
    }
  ]
})

const allOptionsList = computed<Array<IOptions>>(() => {
  return downloadOptionsList.value.concat([
    {
      icon: 'expand',
      label: 'Full Screen',
      action: openChartWindow,
    },
    {
      icon: 'share',
      label: 'Share',
      action: () => {
        showAllOptions.value = false
        showSocialShareModal.value = true
      }
    }
  ])
})

const openChartWindow = () => {
  window.open(routes.getChartPage(props.name), '_blank', 'width=1200,height=604,toolbar=no,menubar=no,status=no')
  showAllOptions.value = false
}

onClickOutside(downloadOptionsRef, () => {
  showDownloadOptions.value = false
})

onClickOutside(optionsRef, () => {
  showAllOptions.value = false
})
</script>

<style scoped lang="scss">
.options-list {
  @apply absolute right-0 top-[calc(100%+0.5rem)] z-[3000] w-fit rounded-lg bg-primary/10 backdrop-blur-lg;
}

.option-button {
  @apply relative;

  &.desktop {
    @include sm {
      @apply hidden;
    }
  }

  &.mobile {
    @apply hidden;
    @include sm {
      @apply inline-flex;
    }
  }
}

.share-modal {
  @apply z-[3000] absolute top-[calc(100%+0.5rem)] right-0;

  @include sm {
    @apply fixed top-auto bottom-4 left-1/2 -translate-x-1/2;
  }
}

</style>